<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';

const { provinces, clientContactRelationshipTypes } = storeToRefs(useOptionsStore());

const props = defineProps({
    initialContacts: {
        type: Array,
        default: () => []
    }
});

const contacts = ref([]);

onMounted(() => {
    contacts.value = props.initialContacts;
});

const emit = defineEmits(['contacts-updated']);
watch(contacts, (newVal) => {
    emit('contacts-updated', newVal);
}, { deep: true });

const addContact = () => {
    contacts.value.push({
        relationship_type_id: null,
        first_name: '',
        last_name: '',
        telephone_number: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        province_id: null,
        relationship_other_description: null
    });
};

const removeContact = (index) => {
    contacts.value.splice(index, 1);
};

const { profile } = storeToRefs(useUserStore());
const roles = computed(() => isRole(profile.value));

const allowEdit = computed(() => {
    if (roles.value.clientSupportWorker()) {
        return false;
    }
    return true;
});
</script>

<template>
<div>
    <div v-for="(contact, index) in contacts" :key="index" class="border-b border-gray p-6 mb-4">
        <div class="md:grid lg:grid-cols-2 xl:grid-cols-3 md:gap-4">
            <StyledDropdown 
                v-model="contact.relationship_type_id"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :options="clientContactRelationshipTypes"
                :label="$t('label.relationship')"
                :name="`contact.${index}.relationship_type_id`" />
            <StyledInput
                v-if="clientContactRelationshipTypes.find(rel => rel.label == 'Other' && rel.value == contact.relationship_type_id)"
                v-model="contact.relationship_other_description"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.relationship_description')"
                :name="`contacts.${index}.relationship_description`" />
            <StyledInput 
                v-model="contact.first_name"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.first_name')"
                :name="`contacts.${index}.first_name`" />
            <StyledInput 
                v-model="contact.last_name"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.last_name')"
                :name="`contacts.${index}.last_name`" />
            <StyledInput 
                v-model="contact.telephone_number"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.phone')"
                :name="`contacts.${index}.telephone_number`" />
            <StyledInput 
                v-model="contact.email"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.email')"
                :name="`contacts.${index}.email`" />
            <StyledInput 
                v-model="contact.address"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.address')"
                :name="`contacts.${index}.address`" />
            <StyledInput 
                v-model="contact.city"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.city')"
                :name="`contacts.${index}.city`" />
            <StyledInput 
                v-model="contact.postal_code"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :label="$t('label.postal_code')"
                :name="`contacts.${index}.postal_code`" />
            <StyledDropdown 
                v-model="contact.province_id"
                :readonly="!allowEdit"
                :clearable="allowEdit"
                :options="provinces"
                :label="$t('label.province')"
                :name="`contacts.${index}.province_id`"
                item-value="id"
                item-title="name" />
            <StyledButton
                v-if="allowEdit"
                color="red" 
                size="small"    
                class="w-24"
                @click="removeContact(index)">
                {{ $t('label.remove') }}
            </StyledButton>
        </div>
    </div>
    <div v-if="allowEdit" class="bg-grey-200 my-4 pb-12 pt-12 px-12 border-black border border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-100" @click="addContact">
        <StyledButton class="flex items-center">
            <v-icon class="mr-2 text-white" icon="mdi-plus" />
            {{ $t('label.add_contact') }}
        </StyledButton>
    </div>
</div>
</template>
    
